import { type Ref, ref } from "vue";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { FIVE_MINUTES, TEN_MINUTES } from "@/helpers/constants/time";
import { useClients } from "@/hooks/clients";
import applicationsApiService from "@/services/modules/applications";
import clientsApiService from "@/services/modules/clients";

import type { BrandName } from "@/schemas/brandName";

const BRAND_NAMES_KEYS = {
  ALL: "brand-names",
} as const;

export const useBrandNamesQuery = (
  search: Ref<string> = ref(""),
  enabled: Ref<boolean> = ref(true)
) => {
  return useQuery({
    queryKey: [BRAND_NAMES_KEYS.ALL, "list", search],
    queryFn: () =>
      applicationsApiService.getBrandNames({
        search: search.value,
      }),
    enabled,
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
  });
};

export const useClientBrandNamesQuery = (enabled: Ref<boolean> = ref(true)) => {
  const { activeClient } = useClients();

  return useQuery({
    queryKey: [BRAND_NAMES_KEYS.ALL, "client", () => activeClient.value?.id],
    queryFn: () => clientsApiService.getBrandNames(activeClient.value!.id),
    enabled: () => enabled.value && !!activeClient.value?.id,
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
  });
};

export const useCreateBrandNameMutation = () => {
  const queryClient = useQueryClient();
  const { activeClient } = useClients();

  return useMutation({
    mutationFn: (data: { name: string }) =>
      clientsApiService.createBrandName(activeClient.value!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BRAND_NAMES_KEYS.ALL],
      });
    },
  });
};

export const useUpdateBrandNameMutation = () => {
  const queryClient = useQueryClient();
  const { activeClient } = useClients();

  return useMutation({
    mutationFn: ({
      brandNameId,
      data,
    }: {
      brandNameId: BrandName["id"];
      data: { name: string };
    }) =>
      clientsApiService.updateBrandName(
        activeClient.value!.id,
        brandNameId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BRAND_NAMES_KEYS.ALL],
      });
    },
  });
};

export const useDeleteBrandNameMutation = () => {
  const queryClient = useQueryClient();
  const { activeClient } = useClients();

  return useMutation({
    mutationFn: (brandNameId: BrandName["id"]) =>
      clientsApiService.deleteBrandName(activeClient.value!.id, brandNameId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BRAND_NAMES_KEYS.ALL],
      });
    },
  });
};
